import { useEffect, useRef, useState } from "react";
import { Plus, Trash2, Edit3, UploadCloud, Loader2, X, Save } from "lucide-react";
import { menuApi } from "@/lib/menuApi";
import { toast } from "sonner";
import { confirmDialog } from "@/components/ConfirmDialog";

/**
 * Admin — Menu Designer Backgrounds & Templates
 *
 * Handles the CRUD flow retail users depend on. Uploads go through
 * /api/admin/menu-templates as multipart form data — the same object storage
 * pipeline the rest of the DAM uses. Every field an admin sets here shapes
 * what retail sees in the New Menu picker (canvas size, orientation, states,
 * active flag).
 */
export default function AdminMenuTemplates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const list = await menuApi.listTemplates();
      setTemplates(list);
    } catch {
      toast.error("Could not load templates");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function handleDelete(t) {
    const ok = await confirmDialog({ title: `Delete "${t.name}"?`, description: "This background will no longer be selectable by retailers." });
    if (!ok) return;
    try {
      await menuApi.deleteTemplate(t.id);
      setTemplates((prev) => prev.filter((x) => x.id !== t.id));
      toast.success("Deleted");
    } catch {
      toast.error("Delete failed");
    }
  }

  async function handleToggleActive(t) {
    try {
      const updated = await menuApi.updateTemplate(t.id, { active: !t.active });
      setTemplates((prev) => prev.map((x) => (x.id === t.id ? updated : x)));
    } catch {
      toast.error("Update failed");
    }
  }

  return (
    <div className="space-y-6" data-testid="admin-menu-templates">
      <div className="flex items-end justify-between">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Menu Designer Management</div>
          <h2 className="font-display text-2xl font-bold mt-1">Backgrounds & Templates</h2>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">Upload the approved backgrounds retailers can use. Only Active templates matching a retailer's format and state appear in their picker.</p>
        </div>
        <button
          onClick={() => { setEditing(null); setModalOpen(true); }}
          className="bg-foreground text-background px-4 py-2 uppercase text-xs font-semibold tracking-widest flex items-center gap-2"
          data-testid="template-new-btn"
        >
          <Plus className="h-3.5 w-3.5" /> New Template
        </button>
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…</div>
      ) : templates.length === 0 ? (
        <div className="border border-dashed border-border rounded-sm p-12 text-center text-sm text-muted-foreground">
          No templates uploaded yet.
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {templates.map((t) => (
            <div key={t.id} className="border border-border rounded-sm bg-surface overflow-hidden" data-testid={`admin-template-${t.id}`}>
              <div className="aspect-[3/4] bg-muted relative">
                {t.thumbnail_url && <img src={t.thumbnail_url} alt={t.name} className="w-full h-full object-cover" />}
                {!t.active && (
                  <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                    <span className="text-xs font-mono uppercase tracking-widest text-white">Inactive</span>
                  </div>
                )}
              </div>
              <div className="p-3">
                <div className="text-sm font-semibold truncate">{t.name}</div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-1">
                  {t.canvas_width} × {t.canvas_height} · {t.orientation}
                </div>
                {t.states?.length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {t.states.map((s) => <span key={s} className="px-1.5 py-0.5 text-[9px] border border-border rounded-full font-mono uppercase tracking-widest">{s}</span>)}
                  </div>
                )}
                <div className="mt-3 flex items-center gap-1.5">
                  <button onClick={() => { setEditing(t); setModalOpen(true); }} className="p-1.5 border border-border rounded-sm hover:bg-surface-elev" title="Edit"><Edit3 className="h-3 w-3" /></button>
                  <button onClick={() => handleToggleActive(t)} className="px-2 py-1 text-[10px] font-mono uppercase tracking-widest border border-border rounded-sm hover:bg-surface-elev">
                    {t.active ? "Deactivate" : "Activate"}
                  </button>
                  <button onClick={() => handleDelete(t)} className="ml-auto p-1.5 rounded-sm text-white" style={{ background: "#E15C2C" }} title="Delete" data-testid={`template-delete-${t.id}`}><Trash2 className="h-3 w-3" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {modalOpen && (
        <TemplateModal
          existing={editing}
          onClose={() => setModalOpen(false)}
          onSaved={() => { setModalOpen(false); load(); }}
        />
      )}
    </div>
  );
}

function TemplateModal({ existing, onClose, onSaved }) {
  const [name, setName] = useState(existing?.name || "");
  const [canvasWidth, setCanvasWidth] = useState(existing?.canvas_width || 850);
  const [canvasHeight, setCanvasHeight] = useState(existing?.canvas_height || 1100);
  const [orientation, setOrientation] = useState(existing?.orientation || "portrait");
  const [colorFamily, setColorFamily] = useState(existing?.color_family || "");
  const [campaign, setCampaign] = useState(existing?.campaign || "");
  const [statesStr, setStatesStr] = useState((existing?.states || []).join(", "));
  const [active, setActive] = useState(existing?.active !== false);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!name.trim()) return toast.error("Name is required");
    if (!existing && !file) return toast.error("Please choose a background file");
    setSaving(true);
    try {
      if (existing) {
        await menuApi.updateTemplate(existing.id, {
          name,
          canvas_width: parseInt(canvasWidth, 10),
          canvas_height: parseInt(canvasHeight, 10),
          orientation,
          color_family: colorFamily || null,
          campaign: campaign || null,
          states: statesStr.split(",").map((s) => s.trim()).filter(Boolean),
          active,
        });
        toast.success("Template updated");
      } else {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("name", name);
        fd.append("canvas_width", String(canvasWidth));
        fd.append("canvas_height", String(canvasHeight));
        fd.append("orientation", orientation);
        fd.append("color_family", colorFamily);
        fd.append("campaign", campaign);
        fd.append("states", statesStr);
        fd.append("active", String(active));
        await menuApi.createTemplate(fd);
        toast.success("Template uploaded");
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <form
        onSubmit={handleSubmit}
        onClick={(e) => e.stopPropagation()}
        className="bg-surface border border-border rounded-sm w-full max-w-lg max-h-[90vh] overflow-y-auto"
        data-testid="template-modal"
      >
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Menu Designer</div>
            <div className="font-display text-xl font-bold">{existing ? "Edit Template" : "New Template"}</div>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 hover:bg-surface-elev rounded-sm"><X className="h-4 w-4" /></button>
        </div>

        <div className="p-4 space-y-4">
          {!existing && (
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-2">Background File</div>
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                className="w-full border border-dashed border-border rounded-sm p-6 text-center hover:bg-surface-elev transition"
                data-testid="template-file-btn"
              >
                <UploadCloud className="h-6 w-6 mx-auto mb-2 text-muted-foreground" />
                <div className="text-sm">{file ? file.name : "Click to choose PNG or JPG"}</div>
              </button>
              <input
                ref={inputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="hidden"
                data-testid="template-file-input"
              />
            </div>
          )}

          <Field label="Name" value={name} onChange={setName} required testid="template-name" />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Canvas Width" value={canvasWidth} onChange={setCanvasWidth} type="number" testid="template-width" />
            <Field label="Canvas Height" value={canvasHeight} onChange={setCanvasHeight} type="number" testid="template-height" />
          </div>
          <SelectField label="Orientation" value={orientation} onChange={setOrientation} options={["portrait", "landscape"]} testid="template-orientation" />
          <Field label="Color Family" value={colorFamily} onChange={setColorFamily} placeholder="e.g. black, magenta" testid="template-color" />
          <Field label="Campaign" value={campaign} onChange={setCampaign} placeholder="optional" testid="template-campaign" />
          <Field label="Approved States (comma-separated)" value={statesStr} onChange={setStatesStr} placeholder="MA, NY, GEN" testid="template-states" />
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} data-testid="template-active" />
            <span>Active</span>
          </label>
        </div>

        <div className="p-4 border-t border-border flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 border border-border rounded-sm uppercase text-xs font-semibold tracking-widest">Cancel</button>
          <button
            type="submit"
            disabled={saving}
            className="bg-foreground text-background px-4 py-2 uppercase text-xs font-semibold tracking-widest disabled:opacity-40 flex items-center gap-2"
            data-testid="template-save-btn"
          >
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, placeholder, testid }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">{label}{required && " *"}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1 w-full bg-transparent border border-border rounded-sm px-3 py-2 text-sm"
        data-testid={testid}
      />
    </label>
  );
}

function SelectField({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full bg-transparent border border-border rounded-sm px-3 py-2 text-sm"
        data-testid={testid}
      >
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}
